import type { PDFDocumentProxy } from "pdfjs-dist";
import { useCallback, useRef, useState } from "react";
import useDebounce from "../../../hooks/useDebounce";
import useResizeObserver from "../../../hooks/useResizeObserver";
import { sessionStorageService } from "../../../services/storage";
import type { StateAction } from "../../../types";
import { preserveScrollPosition } from "../../../utilities/scrollHelper";

interface PdfViewerSettings {
  scalePercent: number;
}

const PDF_VIEWER_SETTINGS_KEY = "pdf_viewer_settings_v2";

const getPdfViewerSettings = () =>
  sessionStorageService.getItemWithFallback<PdfViewerSettings>(PDF_VIEWER_SETTINGS_KEY, { scalePercent: 100 });

const savePdfViewerSettings = (settings: PdfViewerSettings) =>
  sessionStorageService.setItem(PDF_VIEWER_SETTINGS_KEY, settings);

export interface PdfViewerState {
  numPages: number;
  scalePercent: number;
  rotationDegrees: number;
  showThumbnails: boolean;
  currentPageNumber: number;
  documentProxy?: PDFDocumentProxy;
  isDocumentLoaded: boolean;
  loadingProgress: number;
}

const getInitialState = (): PdfViewerState => {
  const userSettings = getPdfViewerSettings();

  return {
    numPages: 0,
    currentPageNumber: 0,
    showThumbnails: false,
    isDocumentLoaded: false,
    loadingProgress: 0,
    rotationDegrees: 0,
    ...userSettings,
  };
};

const minScalePercent = 20;
const maxScalePercent = 500;
const scaleStepPercent = 10;

// Actions

const updateLoadingProgressAction =
  (loadingProgress: number): StateAction<PdfViewerState> =>
  (state) => ({
    ...state,
    loadingProgress,
  });

const documentLoadAction =
  (documentProxy: PDFDocumentProxy): StateAction<PdfViewerState> =>
  (state) => ({
    ...state,
    documentProxy,
    numPages: documentProxy.numPages,
    currentPageNumber: documentProxy.numPages > 0 ? 1 : 0,
    isDocumentLoaded: documentProxy.numPages > 0,
  });

const zoomInAction = (): StateAction<PdfViewerState> => (state) => {
  const scalePercent = Math.min(state.scalePercent + scaleStepPercent, maxScalePercent);

  savePdfViewerSettings({ scalePercent });

  return {
    ...state,
    scalePercent,
  };
};

const zoomOutAction = (): StateAction<PdfViewerState> => (state) => {
  const scalePercent = Math.max(state.scalePercent - scaleStepPercent, minScalePercent);

  savePdfViewerSettings({ scalePercent });

  return {
    ...state,
    scalePercent,
  };
};

const rotateLeftAction = (): StateAction<PdfViewerState> => (state) => {
  return {
    ...state,
    rotationDegrees: (state.rotationDegrees + 270) % 360,
  };
};

const rotateRightAction = (): StateAction<PdfViewerState> => (state) => {
  return {
    ...state,
    rotationDegrees: (state.rotationDegrees + 90) % 360,
  };
};

const toggleThumbnailsAction = (): StateAction<PdfViewerState> => (state) => ({
  ...state,
  showThumbnails: !state.showThumbnails,
});

const updateCurrentPageNumberAction =
  (currentPageNumber: number): StateAction<PdfViewerState> =>
  (state) => ({
    ...state,
    currentPageNumber,
  });

// Hook

interface Size {
  width: number;
  height: number;
}

export const usePdfViewer = () => {
  const pageElementsRef = useRef<HTMLElement[] | null>([]);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  const [viewerState, setViewerState] = useState(getInitialState);
  const [containerSize, setContainerSize] = useState<Size>();

  const onResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      const [containerEntry] = entries;
      if (!containerEntry) {
        return;
      }

      if (containerSize === undefined) {
        setContainerSize({ height: containerEntry.contentRect.height, width: containerEntry.contentRect.width });
        return;
      }

      const threshold = 64;

      if (
        Math.abs(containerEntry.contentRect.height - containerSize.height) > threshold ||
        Math.abs(containerEntry.contentRect.width - containerSize.width) > threshold
      ) {
        setContainerSize({ height: containerEntry.contentRect.height, width: containerEntry.contentRect.width });
      }
    },
    [containerSize]
  );

  useResizeObserver(containerEl, {}, onResize);

  const getCurrentPageIndex = useCallback(() => {
    if (!containerEl || !pageElementsRef.current) {
      return -1;
    }

    const pageTops = pageElementsRef.current.filter(Boolean).map((pageRef) => pageRef.getBoundingClientRect().top);
    if (pageTops.length === 0) {
      return -1;
    }

    const firstVisiblePageTopIndex = pageTops.findIndex((top) => top > 0);
    if (firstVisiblePageTopIndex === -1) {
      return pageTops.length - 1; // All page tops are above the viewport, return last page index
    }

    const firstVisiblePageTop = pageTops[firstVisiblePageTopIndex];
    if (
      firstVisiblePageTopIndex > 0 &&
      containerSize?.height &&
      firstVisiblePageTop !== undefined &&
      firstVisiblePageTop - containerEl.getBoundingClientRect().top > containerSize.height / 2
    ) {
      return firstVisiblePageTopIndex - 1; // The previous page is more than half visible, return its index
    }

    return firstVisiblePageTopIndex;
  }, [containerEl, containerSize]);

  const handleScroll = useDebounce(() => {
    const currentPageIndex = getCurrentPageIndex();

    if (
      currentPageIndex >= 0 &&
      currentPageIndex < viewerState.numPages &&
      currentPageIndex !== viewerState.currentPageNumber - 1
    ) {
      setViewerState(updateCurrentPageNumberAction(currentPageIndex + 1));
    }
  }, 200);

  const navigateToPage = useCallback(
    (pageNumber: number) => {
      setViewerState(updateCurrentPageNumberAction(pageNumber));
      pageElementsRef.current?.[pageNumber - 1]?.scrollIntoView({ behavior: "auto", block: "nearest" });
    },
    [pageElementsRef]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const nextPageNumber =
        e.key === "ArrowLeft"
          ? viewerState.currentPageNumber - 1
          : e.key === "ArrowRight"
            ? viewerState.currentPageNumber + 1
            : undefined;

      if (nextPageNumber !== undefined && nextPageNumber >= 1 && nextPageNumber <= viewerState.numPages) {
        e.preventDefault();
        navigateToPage(nextPageNumber);
      }
    },
    [navigateToPage, viewerState.currentPageNumber, viewerState.numPages]
  );

  const toggleThumbnails = useCallback(() => {
    setViewerState(toggleThumbnailsAction());
  }, []);

  const handleDocumentLoaded = useCallback((documentProxy: PDFDocumentProxy) => {
    setViewerState(documentLoadAction(documentProxy));
  }, []);

  const updateLoadingProgress = useCallback((progress: number) => {
    setViewerState(updateLoadingProgressAction(progress));
  }, []);

  const zoomIn = useCallback(() => {
    const restoreScrollPosition = preserveScrollPosition(containerEl);
    setViewerState(zoomInAction());
    restoreScrollPosition();
  }, [containerEl]);

  const zoomOut = useCallback(() => {
    const restoreScrollPosition = preserveScrollPosition(containerEl);
    setViewerState(zoomOutAction());
    restoreScrollPosition();
  }, [containerEl]);

  const rotateLeft = useCallback(() => {
    setViewerState(rotateLeftAction());
  }, []);

  const rotateRight = useCallback(() => {
    setViewerState(rotateRightAction());
  }, []);

  const resetState = useCallback(() => {
    setViewerState(getInitialState());
  }, []);

  return {
    viewerState,
    containerEl,
    setContainerEl,
    pageRefs: pageElementsRef,
    containerSize,
    handleScroll,
    handleKeyDown,
    navigateToPage,
    toggleThumbnails,
    handleDocumentLoaded,
    updateLoadingProgress,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
    resetState,
  };
};
